import { TextButton } from '@components/Buttons';
import { CodeEditorInput } from '@components/CodeEditor/CodeEditorInput';
import { Box, Grid, Stack } from '@mui/material';
import DynamicValueHelperText from '@components/Tooltips/DynamicValuesHelperText';
import { FormFieldType, IFormFieldRow } from 'types/common';
import { isEmpty } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useBoolean } from 'usehooks-ts';
import { CODE_EDITOR_SUPPORTED_LANG } from '@components/CodeEditor/types';
import FieldRows, { IFieldRowsProps, emptyFieldRow } from './FieldRows';
import { getContextKeys } from './FieldRow';

const isContext = (v: any): boolean =>
	`${v}`.startsWith('{{') && `${v}`.endsWith('}}');

const parseValue = (row: IFormFieldRow): any => {
	switch (row.type) {
		case FormFieldType.string:
			try {
				return JSON.parse(row.value);
				// eslint-disable-next-line no-empty
			} catch (e) {}
			return row.value;
		case FormFieldType.context:
			if (row.value && !isContext(row.value)) return `{{${row.value}}}`;
			return row.value;
		default:
			return row.value;
	}
};

const getFormFieldType = (v: any) => {
	switch (typeof v) {
		case 'number':
			return FormFieldType.number;
		case 'boolean':
			return FormFieldType.boolean;
		case 'string':
		default: {
			if (isContext(v)) {
				return FormFieldType.context;
			}
			return FormFieldType.string;
		}
	}
};

const FieldRowsAdvanced: FC<IFieldRowsProps> = ({
	name,
	onChange,
	rows,
	formik,
	...props
}: IFieldRowsProps) => {
	const { value: isJSONView, setTrue, setFalse } = useBoolean(false);

	const currentValue = useMemo(
		() =>
			JSON.stringify(
				Object.values(rows).reduce(
					(prev, curr) => ({
						...prev,
						...(curr.key
							? {
									[curr.key]: parseValue(curr)
							  }
							: {})
					}),
					{}
				),
				null,
				' '
			),
		[rows]
	);
	const [data, setData] = useState(currentValue);
	const onChangeAdvanced = useCallback(
		(_: any, text: string) => {
			if (!isJSONView) return;
			try {
				const json = JSON.parse(text);
				if (json) {
					const value = Object.entries(json).map(([key, v]) => {
						const t = getFormFieldType(v);
						return {
							key,
							value: isContext(v)
								? (v as string).substring(2, (v as string).length - 2)
								: v,
							type: t
						};
					});
					onChange(isEmpty(value) ? [emptyFieldRow] : value);
				}
			} catch (e) {
				formik.setFieldError(name, 'error');
			} finally {
				setData(text);
			}
		},
		[setData, isJSONView, name, formik, onChange]
	);

	const handleToggle = useCallback(() => {
		if (isJSONView) {
			onChangeAdvanced(null, data);
			setFalse();
		} else {
			setData(currentValue);
			setTrue();
		}
	}, [isJSONView, setTrue, setFalse, onChangeAdvanced, data, currentValue]);

	const dynamicValuesHelpText = useMemo(
		() => (
			<Grid item xs={12}>
				<DynamicValueHelperText
					content={{
						footer: '',
						header: intl.tr('flows.builder.task.dialog.action.json.header')
					}}
					meta={{
						example: intl.tr('flows.builder.task.dialog.action.json.example'),
						trigger: intl.tr('button.usingDynamicValues')
					}}
				>
					{[{ children: getContextKeys().map((v) => v.value) }]}
				</DynamicValueHelperText>
			</Grid>
		),
		[]
	);

	return (
		<Stack pt={1}>
			<TextButton
				onClick={handleToggle}
				sx={{
					textDecoration: 'none',
					justifyContent: 'flex-end',
					p: 0,
					pr: 0.5
				}}
				data-testid="field-rows-advanced-button"
			>
				{intl.tr(`input.object.${!isJSONView ? 'advanced' : 'simple'}`)}
			</TextButton>
			<FieldRows
				name={name}
				rows={rows}
				formik={formik}
				onChange={onChange}
				{...props}
				sx={{ display: isJSONView ? 'none' : 'flex' }}
			/>
			<Box
				py={1}
				flexGrow={1}
				flexDirection="column"
				data-testid="field-rows-editor-container"
				display={isJSONView ? 'flex' : 'none'}
			>
				<Box flexGrow={1}>
					<CodeEditorInput
						id={name}
						editable
						onChange={onChangeAdvanced}
						label=""
						containerSx={{ maxHeight: 350, height: 350 }}
						values={[{ value: data, id: name }]}
						lang={CODE_EDITOR_SUPPORTED_LANG.json}
					/>
				</Box>
				{dynamicValuesHelpText}
			</Box>
		</Stack>
	);
};

export default FieldRowsAdvanced;
